import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { iiafe } from '../../../../../utils/async';
import { Geometry } from '../../../../../viewer/elements/geometry';
import { SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { Point2d } from '../../../../../viewer/utilities/math-helpers';
import { useDialog } from '../../../../hooks/use-dialog';
import { useNonce } from '../../../../hooks/use-nonce';
import { useSharedView } from '../../../../hooks/use-shared-view';
import { useSubscribeCallback } from '../../../../hooks/use-subscribe';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { Icon } from '../../../icon/icon';
import { Table } from '../../../table/table';
import { TableBody } from '../../../table/table-body';
import { TableCell } from '../../../table/table-cell';
import { TableHead } from '../../../table/table-head';
import { TableHeaderCell } from '../../../table/table-header-cell';
import { StyledTableRow, TableRow } from '../../../table/table-row';
import { TextBox } from '../../../text-box/text-box';
import { getAssetDialogPosition, getBesideAssetMenuPosition } from './asset-dialog-position';
import { canEditPoints, Item, useAssetPoints } from './use-asset-points';

type Props = {
  asset: Geometry;
  dialog: ReturnType<typeof useDialog>;
  /**
   * Where the icon was clicked: the dialog opens beside it, near the top of the screen. Without it,
   * when opened as the asset is selected, the dialog opens collapsed beside the asset menu.
   */
  pos?: Point2d;
};

type IconProps = {
  asset: Geometry;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
};

export const PointListIcon = (props: IconProps) => {
  const { t } = useTranslation();
  // The points of an area with a volume calculation can't be edited, only listed.
  const hasVolume = !props.asset.editable;

  return (
    <Icon
      disabled={props.disabled}
      fixedWidth={true}
      icon={['fal', 'draw-polygon']}
      mouseDownStyle={{ opacity: 0.5 }}
      stopPropagationOnClick={true}
      title={
        hasVolume
          ? t('assetPointsPanel.icon.hasVolume', { ns: 'components' })
          : t('assetPointsPanel.icon.show', { ns: 'components' })
      }
      onClick={props.onClick}
      onHoverStyle={{ icon: ['fad', 'draw-polygon'] }}
    />
  );
};

/**
 * Closes the dialog when the points of its asset can no longer be hovered in the scene: the asset
 * is unselected or hidden.
 */
const useCloseWhenNotShown = (asset: Geometry, close: () => void) => {
  const closeIfNotShown = () => {
    if (!asset.isSelected() || !asset.getStateVisibility()) {
      close();
    }
  };

  useSubscribeCallback(SubscriptionTopic.UnSelection, close);
  useSubscribeCallback(SubscriptionTopic.NewSelection, closeIfNotShown);
  useSubscribeCallback(SubscriptionTopic.SkyviewAssetVisibilityChanged, closeIfNotShown);
};

/**
 * Opens the point list of a line or area when it is selected, beside the asset menu, as the points
 * panel before it did. It can be closed, and opened again from its icon.
 */
export const useOpenPointListOnSelection = (
  asset: Geometry,
  dialog: ReturnType<typeof useDialog>,
) => {
  useSubscribeCallback(SubscriptionTopic.NewSelection, (selected) => {
    // When opened from the icon, which also selects the asset, it stays beside the icon.
    if (!dialog.visible && selected.uuid === asset.uuid && asset.getStateVisibility()) {
      dialog.show();
    }
  });
};

/**
 * Lists the points of a line or area, which can be edited. The list and the scene are kept in sync:
 * hovering a point highlights it in both, and the points move as they are edited. The points are
 * only listed, not edited, in a shared view, and while the area has a volume calculation or a
 * volume tool uses it.
 */
export const PointListDialog = (props: Props) => {
  const { t } = useTranslation();
  const { asset } = props;
  const { isSharedView } = useSharedView();
  const [_, refresh] = useNonce();
  const {
    currentItems,
    flattenGeometry,
    orderByItemIds,
    originalItems,
    restoreAllItems,
    restoreItem,
    updateEasting,
    updateMas,
    updateNorthing,
  } = useAssetPoints(asset);
  useCloseWhenNotShown(asset, props.dialog.hide);
  // Whether the points can be edited changes with the volume calculation and the tool.
  useSubscribeCallback(SubscriptionTopic.AssetChanged, refresh);
  useSubscribeCallback(SubscriptionTopic.SetTool, refresh);
  const readOnly = isSharedView || !canEditPoints(asset);

  const isItemChanged = (item1: Item, item2: Item) =>
    item1.e.value !== item2.e.value ||
    item1.n.value !== item2.n.value ||
    item1.mas.value !== item2.mas.value;

  const anyItemsChanged = () =>
    [...originalItems.entries()].some(([id, value]) => isItemChanged(value, currentItems.get(id)!));

  const isGeometryFlattened =
    new Set([...currentItems.values()].map((x) => x.mas.value)).size === 1;

  return (
    <Dialog
      // Beside the asset menu, below the other dialogs there, as it opens as the asset is selected.
      autoDock={!props.pos}
      closeIcon={true}
      collapsible={true}
      defaultCollapsed={!props.pos}
      disableDimmer={true}
      // Opened from the icon again, e.g. after it was opened collapsed as the asset was selected:
      // it opens beside the icon, unfolded.
      key={props.pos ? `${props.pos.x},${props.pos.y}` : undefined}
      movable={true}
      position={props.pos ? getAssetDialogPosition(props.pos) : getBesideAssetMenuPosition()}
      onClose={props.dialog.hide}
    >
      {{
        header: asset.name,
        content: (
          <Body>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell padding={0.5}>
                    {t('assetPointsPanel.tableHeader.northing', { ns: 'components' })}
                  </TableHeaderCell>
                  <TableHeaderCell padding={0.5}>
                    {t('assetPointsPanel.tableHeader.easting', { ns: 'components' })}
                  </TableHeaderCell>
                  <TableHeaderCell padding={0.5}>
                    {t('assetPointsPanel.tableHeader.height', { ns: 'components' })}
                  </TableHeaderCell>
                  {!readOnly && (
                    <>
                      <TableHeaderCell padding={0.5} />
                      <TableHeaderCell padding={0.5}>
                        <Icon
                          disabled={!anyItemsChanged()}
                          fixedWidth={true}
                          icon={['fal', 'arrow-rotate-left']}
                          title={t('assetPointsPanel.icon.undoAllChanges', { ns: 'components' })}
                          onClick={restoreAllItems}
                          onHoverStyle={{ icon: ['fad', 'arrow-rotate-left'] }}
                        />
                      </TableHeaderCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {orderByItemIds.map((id, index) => {
                  const currentPoint = currentItems.get(id)!;
                  const isChanged = isItemChanged(originalItems.get(id)!, currentItems.get(id)!);

                  const flattenIconDisabled =
                    isGeometryFlattened ||
                    !currentPoint.e.isValid ||
                    !currentPoint.n.isValid ||
                    !currentPoint.mas.isValid;

                  return (
                    <TableRow
                      data-selected={asset.highlightedIndex === index}
                      key={id}
                      onMouseEnter={() => asset.mouseOver(index)}
                      onMouseLeave={() => asset.mouseOver(-1)}
                    >
                      {readOnly ? (
                        <>
                          <TableCell padding={0.5}>
                            <Value>{currentPoint.n.value}</Value>
                          </TableCell>
                          <TableCell padding={0.5}>
                            <Value>{currentPoint.e.value}</Value>
                          </TableCell>
                          <TableCell padding={0.5}>
                            <Value>{currentPoint.mas.value}</Value>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell padding={0.5}>
                            <TextBox
                              errorMessage={currentPoint.n.isValid ? undefined : ''}
                              maxVisibleChars={11}
                              monospaced="roboto"
                              padding={0.25}
                              step={0.01}
                              type="number"
                              value={currentPoint.n.value}
                              onInput={(e) =>
                                iiafe(() => updateNorthing(id, e.currentTarget.value))
                              }
                            />
                          </TableCell>
                          <TableCell padding={0.5}>
                            <TextBox
                              errorMessage={currentPoint.e.isValid ? undefined : ''}
                              maxVisibleChars={11}
                              monospaced="roboto"
                              padding={0.25}
                              step={0.01}
                              type="number"
                              value={currentPoint.e.value}
                              onInput={(e) => iiafe(() => updateEasting(id, e.currentTarget.value))}
                            />
                          </TableCell>
                          <TableCell padding={0.5}>
                            <TextBox
                              errorMessage={currentPoint.mas.isValid ? undefined : ''}
                              maxVisibleChars={8}
                              monospaced="roboto"
                              padding={0.25}
                              step={0.01}
                              type="number"
                              value={currentPoint.mas.value}
                              onChange={(e) => iiafe(() => updateMas(id, e.currentTarget.value))}
                            />
                          </TableCell>
                          <TableCell padding={0.5}>
                            <Icon
                              disabled={flattenIconDisabled}
                              fixedWidth={true}
                              icon={['fal', 'arrows-to-line']}
                              title={
                                flattenIconDisabled
                                  ? undefined
                                  : t('assetPointsPanel.icon.flatten', { ns: 'components' })
                              }
                              onClick={() => iiafe(() => flattenGeometry(id))}
                              onHoverStyle={{ icon: ['fad', 'arrows-to-line'] }}
                            />
                          </TableCell>
                          <TableCell padding={0.5}>
                            <Icon
                              color={'#000'}
                              disabled={!isChanged}
                              fixedWidth={true}
                              icon={['fal', 'arrow-rotate-left']}
                              title={
                                isChanged
                                  ? t('assetPointsPanel.icon.undoChanges', { ns: 'components' })
                                  : undefined
                              }
                              onClick={() => iiafe(() => restoreItem(id))}
                              onHoverStyle={{ icon: ['fad', 'arrow-rotate-left'] }}
                            />
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Body>
        ),
        footer: {
          right: (
            <Button color="primary" type="button" variant="contained" onClick={props.dialog.hide}>
              {t('close', { ns: 'common' })}
            </Button>
          ),
        },
      }}
    </Dialog>
  );
};

const Value = styled.span`
  font-family: 'Roboto Mono';
  font-weight: 300;
  white-space: nowrap;
`;

const Body = styled.div`
  ${StyledTableRow} {
    &:hover {
      background-color: #fff1ae;
    }

    &[data-selected='true'] {
      background-color: ${({ theme }) => theme.color.selected};
    }
  }
`;
